import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { companyInfo } from '../mockData';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="mb-4 bg-white/95 inline-block p-3 rounded-lg">
              <img
                src="/logos/midtown-logo.png"
                alt="Midtown Earth Solutions LLC"
                className="h-20 w-auto object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              Your trusted partner for premium landscaping materials and reliable delivery services in Columbus, GA.
            </p>
            <a
              href={companyInfo.googleBusinessUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors"
              data-testid="footer-google-reviews"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-xs font-semibold text-white">Google Reviews</span>
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm hover:text-green-400 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm hover:text-green-400 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-sm hover:text-green-400 transition-colors">
                  Products
                </Link>
              </li>
              <li>
                <Link to="/delivery" className="text-sm hover:text-green-400 transition-colors">
                  Delivery Services
                </Link>
              </li>
              <li>
                <Link to="/service-areas" className="text-sm hover:text-green-400 transition-colors">
                  Service Areas
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-sm hover:text-green-400 transition-colors">
                  Blog & Guides
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm hover:text-green-400 transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Delivery Services</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services/gravel-delivery" className="hover:text-green-400 transition-colors">Gravel Delivery</Link></li>
              <li><Link to="/services/57-stone-delivery" className="hover:text-green-400 transition-colors">57 Stone Delivery</Link></li>
              <li><Link to="/services/sand-delivery" className="hover:text-green-400 transition-colors">Sand Delivery</Link></li>
              <li><Link to="/services/mulch-delivery" className="hover:text-green-400 transition-colors">Mulch Delivery</Link></li>
              <li><Link to="/services/topsoil-delivery" className="hover:text-green-400 transition-colors">Topsoil Delivery</Link></li>
              <li><Link to="/services/river-rock-delivery" className="hover:text-green-400 transition-colors">River Rock Delivery</Link></li>
              <li><Link to="/calculator" className="hover:text-green-400 transition-colors font-semibold text-green-400">Material Calculator</Link></li>
              <li><Link to="/gallery" className="hover:text-green-400 transition-colors">Gallery</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{companyInfo.address}</span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-green-400 flex-shrink-0" />
                <a href="tel:+17063663685" className="text-sm hover:text-green-400 transition-colors">
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-green-400 flex-shrink-0" />
                <a href="mailto:Richard@midtownearthsolutionsga.com" className="text-sm hover:text-green-400 transition-colors">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{companyInfo.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8 text-center">
          <p className="text-sm text-gray-400">
            &copy; {new Date().getFullYear()} Midtown Earth Solutions LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
